import React, { useState } from 'react';
import { Task, TaskPriority } from '../types';
import { UpdateTaskRequest } from '../services/tasks';

export interface TaskEditModalProps {
  task: Task;
  onSave: (taskId: string, updates: UpdateTaskRequest) => Promise<void>;
  onClose: () => void;
}

/**
 * Modal for editing task properties.
 * Allows editing title, priority, and due date.
 * Preserves caseId (read-only, not editable).
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */
export const TaskEditModal: React.FC<TaskEditModalProps> = ({ task, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: task.title,
    priority: task.priority,
    dueDate: task.dueDate || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const updates: UpdateTaskRequest = {
        title: form.title,
        priority: form.priority,
        dueDate: form.dueDate || null,
      };
      await onSave(task.taskId, updates);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%'
      }}>
        <h3 style={{ marginTop: 0 }}>Edit Task</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                Priority
              </label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value as TaskPriority })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                Due Date
              </label>
              <input
                type="date"
                value={form.dueDate}
                onChange={e => setForm({ ...form, dueDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: 'white',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: saving || !form.title.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: saving || !form.title.trim() ? 0.6 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Applies task edit updates while preserving caseId.
 * Returns the updated task fields without modifying caseId.
 * 
 * Property 9: Task edit preserves caseId and applies updates
 * Validates: Requirements 5.4, 5.5
 */
export function applyTaskEdit(
  task: Task,
  updates: UpdateTaskRequest
): { taskId: string; caseId: string; updates: UpdateTaskRequest } {
  return {
    taskId: task.taskId,
    caseId: task.caseId, // caseId is always preserved
    updates: {
      title: updates.title,
      priority: updates.priority,
      dueDate: updates.dueDate,
    },
  };
}
